<template>
  <div v-if="isOpen" class="dialog-overlay" @click="handleCancel()">
    <div class="dialog-content" @click.stop>
      <header class="dialog-header">
        <h2>Edit Training Session</h2>
        <button class="close-btn" @click="handleCancel()">✕</button>
      </header>

      <div class="dialog-body">
        <div class="form-group">
          <label for="session-name">Session Name</label>
          <input
            id="session-name"
            v-model="editedSession.name"
            type="text"
            class="form-input"
            placeholder="Session name"
          />
        </div>

        <div class="form-group">
          <label for="session-description">Description</label>
          <textarea
            id="session-description"
            v-model="editedSession.description"
            class="form-textarea"
            placeholder="Description"
            rows="4"
          />
        </div>
      </div>

      <footer class="dialog-footer">
        <button class="dialog-button dialog-button-cancel" @click="handleCancel()">
          Cancel
        </button>
        <button
          class="dialog-button dialog-button-save"
          :disabled="!isFormValid"
          @click="handleSave()"
        >
          Save
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';

const $toast = useToast();

export default {
  name: 'SessionEditDialog',
  props: {
    selectedSessions: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['edit-session'],
  data() {
    return {
      isOpen: false,
      editedSession: {
        id: null,
        name: '',
        description: '',
      },
      originalSession: null,
      dataConnect: new DatastoreConnect(this.$store.state.enterpriseServerUrl),
    };
  },
  computed: {
    isFormValid() {
      return this.editedSession.name && this.editedSession.name.trim().length > 0;
    },
  },
  methods: {
    openModal(session) {
      if (!session) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: 'No session selected',
          type: 'warning',
          position: 'top',
        });
        return;
      }

      this.originalSession = JSON.parse(JSON.stringify(session));
      this.editedSession = {
        id: session.id,
        name: session.docker_task?.name || session.name || '',
        description: session.description || '',
      };
      this.isOpen = true;
    },
    closeModal() {
      this.isOpen = false;
    },
    handleCancel() {
      this.closeModal();
    },
    async handleSave() {
      if (!this.isFormValid) {
        return;
      }

      try {
        const resp = await this.dataConnect.updateTrainingSession({
          id: this.editedSession.id,
          name: this.editedSession.name,
          description: this.editedSession.description,
        });

        if (resp && !resp.error) {
          $toast.open({
            queue: true,
            dismissible: true,
            message: 'Training session updated successfully',
            type: 'success',
            position: 'top',
          });

          this.$emit('edit-session', {
            ...this.originalSession,
            id: this.editedSession.id,
            name: this.editedSession.name,
            description: this.editedSession.description,
            docker_task: {
              ...this.originalSession?.docker_task,
              name: this.editedSession.name,
            },
          });
          this.closeModal();
        } else {
          throw new Error(resp?.error?.message || 'Failed to update session');
        }
      } catch (error) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Error: ${error.message}`,
          type: 'error',
          position: 'top',
        });
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/assets/css/themes/auzone/buttons.scss';

.dialog-body {
  .form-group {
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }

    label {
      display: block;
      margin-bottom: 8px;
      font-weight: 500;
      font-size: 14px;
      color: #333;
    }
  }
}

.form-input,
.form-textarea {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(var(--color-primary-rgb), 0.1);
  }
}

.form-textarea {
  resize: vertical;
  min-height: 200px;
}
</style>
